import {
  Docket,
  DocketItem,
  DocketItemStatus,
  KitchenDisplayMode,
} from '@oolio-group/domain';
import { createAttemptGuard } from './actionAttemptGuard';

// Shared across every auto-bump call site so a docket already bumped isn't resubmitted on a later poll cycle.
export const autoBumpAttemptGuard = createAttemptGuard();

/**
 * Checks if a docket should be automatically bumped based on device mode and item statuses
 *
 * @param docket - The docket to check
 * @param mode - The kitchen display mode (PREP or EXPO)
 * @param rebumpToggle - Whether two-level fulfillment (rebump) is enabled
 * @param autoBumpFeature - Feature flag to enable/disable auto-bump functionality
 * @returns true if the docket should be auto-bumped, false otherwise
 */
export const shouldAutoBumpDocket = (
  docket: Docket,
  mode: KitchenDisplayMode,
  rebumpToggle: boolean,
  autoBumpFeature: boolean,
): boolean => {
  // Feature flag check - auto-bump only if enabled
  if (!autoBumpFeature) {
    return false;
  }

  if (!docket.docketItems || docket.docketItems.length === 0) {
    return false;
  }

  return docket.docketItems.every((item: DocketItem) => {
    if (mode === KitchenDisplayMode.EXPO) {
      // EXPO: Only SERVED counts as complete
      return item.itemStatus === DocketItemStatus.SERVED;
    } else {
      // PREP: BUMPED, REBUMPED (if two-level fulfillment), or SERVED
      if (rebumpToggle) {
        // With two-level fulfillment: REBUMPED, or SERVED
        return [DocketItemStatus.REBUMPED, DocketItemStatus.SERVED].includes(
          item.itemStatus,
        );
      } else {
        // Without two-level fulfillment: BUMPED or SERVED
        return [
          DocketItemStatus.BUMPED,
          DocketItemStatus.REBUMPED,
          DocketItemStatus.SERVED,
        ].includes(item.itemStatus);
      }
    }
  });
};

/**
 * Determines the correct docket status for auto-bump based on rebump toggle
 * Matches the behavior of manual header bump
 *
 * @param rebumpToggle - Whether two-level fulfillment (rebump) is enabled
 * @returns The status to set when auto-bumping the docket
 */
export const getAutoBumpStatus = (rebumpToggle: boolean): DocketItemStatus => {
  return rebumpToggle ? DocketItemStatus.REBUMPED : DocketItemStatus.BUMPED;
};
